import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { safeToString } from "_shared/_utilities/safe-to-string";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { Change, diffLines } from "diff";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/_shared/masked-fields";
import ProposalField, { HumanReadableProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import { RedlineAction, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import FeeScheduleCategoryRedlineSchema, {
  FeeScheduleCategoryRedlineJSON,
} from "work/proposals/redlining/fee-schedule-redline/_schema/fee-schedule-category-redline-schema";
import FeeRedline, { FeeType } from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null;

export class FeeScheduleCategoryRedline {
  public nameRedline?: FieldRedline<FeeScheduleCategoryName>;
  public descriptionRedline?: FieldRedline<FeeScheduleCategoryDescription>;
  public billingCodeRedline?: FieldRedline<FeeScheduleBillingCode>;
  public feeRedline?: FeeRedline;

  constructor(
    fieldId: Guid,
    originalEntry: FeeScheduleCategoryEntry,
    revisedEntry: FeeScheduleCategoryEntry,
    currentEntry: FeeScheduleCategoryEntry | undefined = undefined,
  ) {
    this._field = ProposalField.FeeScheduleCategory(fieldId);
    this._originalEntry = originalEntry;
    this._revisedEntry = revisedEntry;
    this._currentEntry = currentEntry;

    this.refreshSubRedlineValues();
    this.updateDiff();
  }

  public get field(): ProposalField {
    return this._field;
  }

  public get label(): string {
    return HumanReadableProposalFieldName[this._field.name];
  }

  public get originalEntry(): FeeScheduleCategoryEntry {
    return this._originalEntry;
  }

  public get revisedEntry(): FeeScheduleCategoryEntry {
    return this._revisedEntry;
  }

  public get currentEntry(): FeeScheduleCategoryEntry | undefined {
    return this._currentEntry;
  }

  public get textChanges(): TextChange[] {
    return [...this._textChangeCollection.textChanges];
  }

  public get textChangeCollection(): TextChangeCollection {
    return this._textChangeCollection;
  }

  public get sessionHistory(): RedlineChange[] {
    return this._sessionHistory;
  }

  public get isAdded(): boolean {
    return (
      (this.originalEntry === null && this.revisedEntry !== null) ||
      (this.revisedEntry === null && this.currentEntry !== null)
    );
  }

  public get isNewlyAdded(): boolean {
    return Boolean(this._currentEntry && !this.wasRedlined && !this._revisedEntry);
  }

  public get wasRemoved(): boolean {
    return this.originalEntry !== null && this.revisedEntry === null;
  }

  public get isRemoved(): boolean {
    return (
      (this.originalEntry !== null && this.revisedEntry === null) ||
      (this.revisedEntry !== null && this.currentEntry === null)
    );
  }

  public get isNewlyRemoved(): boolean {
    return this.revisedEntry !== null && this.currentEntry === null;
  }

  public get isRevised(): boolean {
    return this._currentEntry !== undefined && !this.isAccepted;
  }

  public get isNewlyRevised(): boolean {
    return Boolean(this._currentEntry && !this._revisedEntry?.isEqualTo(this._currentEntry));
  }

  public get wasRedlined(): boolean {
    if (this._originalEntry === null && this._revisedEntry === null) {
      return false;
    } else if (this._revisedEntry === null) {
      return true;
    } else {
      return !this.revisedEntry?.isEqualTo(this.originalEntry);
    }
  }

  public get isResolved(): boolean {
    return this._currentEntry !== undefined;
  }

  public get canBeUndone(): boolean {
    if (this.wasRemoved && !this.isRemoved) return false;
    return (this.isResolved && this.wasRedlined) || this.isNewlyRemoved || this.isNewlyAdded || this.isNewlyRevised;
  }

  public get isAccepted(): boolean {
    if (this._currentEntry === null && this._revisedEntry === null) {
      return true;
    }
    return this._currentEntry?.isEqualTo(this._revisedEntry) ?? false;
  }

  public get isRejected(): boolean {
    return this.isResolved && this.wasRedlined && !this.isAccepted;
  }

  public get hasChanges(): boolean {
    return this.isNewlyAdded || this.isNewlyRevised || this.isRejected || this.isNewlyRemoved;
  }

  public edit(newEntry: FeeScheduleCategoryEntry): FeeScheduleCategoryRedline {
    const newRedline = this.clone();
    if (newEntry?.isEqualTo(this._currentEntry) || (newEntry === null && this._currentEntry === null)) {
      return newRedline;
    }
    newRedline._currentEntry = newEntry;
    newRedline._sessionHistory = [];
    newRedline.updateDiff();

    let action: RedlineAction;
    let changes: TextChange[] = [];
    if (newRedline.isNewlyAdded) {
      action = RedlineAction.Add;
      changes = newRedline.textChanges;
    } else if (newRedline.isNewlyRemoved) {
      action = RedlineAction.Remove;
      changes = this.getInitialChanges();
    } else if (newRedline.wasRedlined && newRedline.isNewlyRevised) {
      action = RedlineAction.ReEdit;
      changes = this.getInitialChanges();
    } else {
      action = RedlineAction.Edit;
      changes = newRedline.textChanges;
    }

    const redlineChange = new RedlineChange({
      field: newRedline.field,
      isResolved: newRedline.isResolved,
      action,
      textChanges: changes,
    });
    newRedline._sessionHistory.push(redlineChange);

    return newRedline;
  }

  public remove(): FeeScheduleCategoryRedline {
    if (this._currentEntry === null) {
      console.warn("Can't remove a redline that hasn't been modified");
      return this.clone();
    }

    const newRedline = this.clone();
    newRedline._sessionHistory = [];
    newRedline._currentEntry = null;
    newRedline.updateDiff();
    const redlineChange = new RedlineChange({
      field: newRedline.field,
      isResolved: newRedline.isResolved,
      action: RedlineAction.Remove,
      textChanges: newRedline.textChanges,
    });
    newRedline._sessionHistory.push(redlineChange);

    return newRedline;
  }

  public accept(): FeeScheduleCategoryRedline {
    const newRedline = this.clone();
    newRedline._sessionHistory = [];
    newRedline._currentEntry = newRedline._revisedEntry;
    newRedline.updateDiff();

    if (!this.isAccepted) {
      const redlineChange = new RedlineChange({
        field: newRedline.field,
        isResolved: true,
        action: RedlineAction.Accept,
        textChanges: newRedline.textChanges,
      });
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  public reject(): FeeScheduleCategoryRedline {
    const newRedline = this.clone();
    newRedline._sessionHistory = [];
    newRedline._currentEntry = newRedline._originalEntry;
    newRedline.updateDiff();

    if (!this.isRejected) {
      const redlineChange = new RedlineChange({
        field: newRedline.field,
        isResolved: true,
        action: RedlineAction.Reject,
        textChanges: newRedline.textChanges,
      });
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  public undo(): FeeScheduleCategoryRedline {
    if (this._currentEntry === undefined) {
      console.warn("Can't undo a redline that hasn't been modified");
      return this.clone();
    }

    const newRedline = this.clone();
    newRedline._sessionHistory = [];
    if (!this.wasRedlined) {
      newRedline._currentEntry = this._revisedEntry;
    } else if (this.isAccepted || this.isRejected) {
      newRedline._currentEntry = undefined;
    } else if (this.isRemoved) {
      newRedline._currentEntry = this._originalEntry;
    } else {
      newRedline._currentEntry = undefined;
    }

    newRedline.updateDiff();

    let action: RedlineAction | undefined;
    let textChanges = newRedline.textChanges;
    let resolved = true;
    if (this.wasRedlined && this.isAccepted) {
      action = RedlineAction.UndoAccept;
      textChanges = this.getInitialChanges();
      resolved = false;
    } else if (this.wasRedlined && this.isRejected) {
      action = RedlineAction.UndoReject;
      textChanges = this.getInitialChanges();
      resolved = false;
    } else if (this.isResolved && this._currentEntry && newRedline._currentEntry == null) {
      action = RedlineAction.Remove;
    } else if (this.isResolved) {
      action = RedlineAction.UndoEdit;
      textChanges = this.textChanges;
    } else {
      return newRedline;
    }

    if (action && !this._sessionHistory.length) {
      const redlineChange = new RedlineChange({
        field: this._field,
        isResolved: resolved,
        action,
        textChanges,
      });
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  public isEqualTo(other: FeeScheduleCategoryRedline): boolean {
    return Boolean(
      ((this._originalEntry === null && other._originalEntry === null) ||
        this._originalEntry?.isEqualTo(other.originalEntry)) &&
      ((this._revisedEntry === null && other._revisedEntry === null) ||
        this._revisedEntry?.isEqualTo(other.revisedEntry)) &&
      ((this._currentEntry === null && other._currentEntry === null) ||
        (this._currentEntry === undefined && other._currentEntry === undefined) ||
        this._currentEntry?.isEqualTo(other.currentEntry)),
    );
  }

  public getInitialChanges(): TextChange[] {
    const changes = new Array<TextChange>();
    const previous = this._originalEntry?.toString() ?? "";
    const next = this._revisedEntry?.toString() ?? "";
    const diff = diffLines(previous, next);

    diff.forEach((change: Change) => {
      changes.push(
        new TextChange({
          parent: this._textChangeCollection,
          diff: change,
        }),
      );
    });
    return changes;
  }

  public clearSessionHistory() {
    this._sessionHistory = [];
  }

  public isCurrentWith(other: FeeScheduleCategoryRedline): boolean {
    if (!this._field.isEqualTo(other.field)) return false;
    if (this._originalEntry === null && other._originalEntry !== null) return false;
    if (this._originalEntry && !this._originalEntry.isEqualTo(other.originalEntry)) return false;
    if (this._revisedEntry === null && other._revisedEntry !== null) return false;
    return !(this._revisedEntry && !this._revisedEntry.isEqualTo(other.revisedEntry));
  }

  public clone(): FeeScheduleCategoryRedline {
    return FeeScheduleCategoryRedline.fromJSON(this.toJSON(), this._field);
  }

  public toJSON(): FeeScheduleCategoryRedlineJSON {
    return FeeScheduleCategoryRedlineSchema.serialize(this);
  }

  public static fromJSON(json: FeeScheduleCategoryRedlineJSON, field: ProposalField): FeeScheduleCategoryRedline {
    if (!field?.id) throw new Error("Field id is required");

    const originalEntry = json.originalEntry ? FeeScheduleCategory.fromJSON(json.originalEntry) : null;
    if (Masked.isMasked(originalEntry)) {
      throw new Error("Original entry cannot be a masked category");
    }
    const revisedEntry = json.revisedEntry ? FeeScheduleCategory.fromJSON(json.revisedEntry) : null;
    const currentEntry = isNullOrUndefined(json.currentEntry)
      ? json.currentEntry
      : FeeScheduleCategory.fromJSON(json.currentEntry);
    let newCategoryRedline = new FeeScheduleCategoryRedline(field.id, originalEntry, revisedEntry, currentEntry);

    const sessionHistory = new Array<RedlineChange>();
    if (json.sessionHistory) {
      json.sessionHistory.forEach((change) => {
        sessionHistory.push(RedlineChange.fromJSON({ field: ProposalField.FeeSchedule, json: change }));
      });
    }

    newCategoryRedline.refreshSubRedlineValues();
    newCategoryRedline.updateDiff();
    newCategoryRedline._sessionHistory = sessionHistory;

    return newCategoryRedline;
  }

  private readonly _field: ProposalField;
  private readonly _originalEntry: FeeScheduleCategoryEntry;
  private readonly _revisedEntry: FeeScheduleCategoryEntry;
  private _currentEntry: FeeScheduleCategoryEntry | undefined;
  private _sessionHistory: RedlineChange[] = [];
  private _textChangeCollection: TextChangeCollection = new TextChangeCollection();

  private updateDiff() {
    let previous = "";
    let next = "";

    if (
      (this._currentEntry === null && this._revisedEntry === null) ||
      this._currentEntry?.isEqualTo(this._revisedEntry)
    ) {
      previous = safeToString(this._originalEntry);
      next = safeToString(this._currentEntry);
    } else if (this._currentEntry || this._currentEntry === null) {
      previous = safeToString(this._revisedEntry);
      next = safeToString(this._currentEntry);
    } else {
      previous = safeToString(this._originalEntry);
      next = safeToString(this._revisedEntry);
    }

    // If nothing to show, bail.
    if (previous === "" && next === "") {
      this.refreshSubRedlineValues();
      return;
    }

    const diff = diffLines(previous, next);
    this._textChangeCollection =
      TextChangeCollection.fromJSONArray(
        diff.map((change) => new TextChange({ parent: this._textChangeCollection, diff: change }).toJSON()),
      ) ?? new TextChangeCollection();

    this.refreshSubRedlineValues();
  }

  private refreshSubRedlineValues() {
    this.refreshNameRedline();
    this.refreshDescriptionRedline();
    this.refreshBillingCodeRedline();
    this.refreshFeeRedline();
  }

  private refreshFeeRedline() {
    let originalFee: FeeType = false;
    let revisedFee: FeeType = false;
    let currentFee: FeeType | undefined = undefined;

    if (
      this._originalEntry instanceof FeeScheduleCategoryMaskedCurrency ||
      this._revisedEntry instanceof FeeScheduleCategoryMaskedCurrency ||
      this._currentEntry instanceof FeeScheduleCategoryMaskedCurrency
    )
      return;

    if (this._originalEntry?.fee === null) {
      originalFee = null;
    } else if (this._originalEntry?.fee) {
      originalFee = this._originalEntry.fee;
    }

    if (this._revisedEntry?.fee === null) {
      revisedFee = null;
    } else if (this._revisedEntry?.fee) {
      revisedFee = this._revisedEntry.fee;
    }

    if (this._currentEntry === null) {
      currentFee = false;
    } else if (this._currentEntry?.fee === null) {
      currentFee = null;
    } else if (this._currentEntry?.fee) {
      currentFee = this._currentEntry.fee;
    }

    this.feeRedline = new FeeRedline(originalFee, revisedFee);
    this.feeRedline = this.feeRedline.updateFee(currentFee);
  }

  private refreshBillingCodeRedline() {
    this.billingCodeRedline = new FieldRedline<FeeScheduleBillingCode>({
      field: ProposalField.FeeSchedule,
      originalEntry: this._originalEntry === null ? null : this._originalEntry?.billingCode,
      revisedEntry: this._revisedEntry === null ? null : this._revisedEntry?.billingCode,
      currentEntry: this._currentEntry === null ? null : this._currentEntry?.billingCode,
      diffWords: true,
    });
  }

  private refreshDescriptionRedline() {
    this.descriptionRedline = new FieldRedline<FeeScheduleCategoryDescription>({
      field: ProposalField.FeeSchedule,
      originalEntry: this._originalEntry === null ? null : this._originalEntry?.description,
      revisedEntry: this._revisedEntry === null ? null : this._revisedEntry?.description,
      currentEntry: this._currentEntry === null ? null : this._currentEntry?.description,
      diffWords: true,
    });
  }

  private refreshNameRedline() {
    this.nameRedline = new FieldRedline<FeeScheduleCategoryName>({
      field: ProposalField.FeeSchedule,
      originalEntry: this._originalEntry === null ? null : this._originalEntry?.name,
      revisedEntry: this._revisedEntry === null ? null : this._revisedEntry?.name,
      currentEntry: this._currentEntry === null ? null : this._currentEntry?.name,
      diffWords: true,
    });
  }
}
